import {
  useBookings,
  useCancelRegistration,
  useCreateTicket,
  useEventInstance,
  useAccess,
  useRecordPayment,
  useRegister,
  useRegistrations,
  useTickets,
  type Registration,
  type Ticket,
} from '@church/data'
import {
  faArrowLeft,
  faCheck,
  faDownload,
  faEnvelope,
  faPlus,
  faTicket,
  faUserMinus,
} from '@church/ui-icons'
import { cents, formatCents, parseCents, toDecimalString } from '@church/domain'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { RequireAccess } from '../components/RequireAccess.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  Input,
  ListHeader,
  listRow,
  PageHeader,
  PanelSection,
  SidePanel,
  Spinner,
  Tabs,
  PanelPrimary,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { EventDetails } from '../components/events/EventDetails.tsx'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { useListDisplay } from '../lib/display.ts'
import { PageAction } from '../components/PageAction.tsx'

/**
 * One date of an event: what tickets it has, who has taken a place, and which
 * rooms are held for it.
 *
 * People register for a date, not for an event, so this is where registration
 * lives. The waitlist is shown as part of the same list rather than tucked
 * away — a registrar wants to see the queue next to the places, because the
 * question is usually "how many more could we take?".
 */
export const Route = createFileRoute('/_authed/_shell/events/instances/$id')({
  component: InstanceScreen,
})

const TABS = ['About', 'Tickets', 'Who is coming', 'Rooms'] as const
type Tab = (typeof TABS)[number]

function InstanceScreen() {
  const { id } = Route.useParams()
  const [tab, setTab] = useState<Tab>('About')
  const access = useAccess()

  const { data: instance, isPending } = useEventInstance(id)
  useBreadcrumbs([{ label: instance ? (instance.title ?? instance.eventTitle) : 'Date' }])

  if (isPending) return <Spinner />
  if (!instance) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="Date not found"
          hint="It may have been cancelled, or it may not be one you can see."
        />
        <Link
          to="/events"
          className="inline-flex items-center gap-2 text-sm text-content-brand hover:underline"
        >
          <Icon icon={faArrowLeft} />
          Back to the calendar
        </Link>
      </div>
    )
  }

  const when = new Date(instance.startsAt)
  const visibleTabs = access.has('events', 'editor') ? TABS : (['About', 'Tickets'] as const)

  return (
    <div className="space-y-6">
      <PageHeader
        title={instance.title ?? instance.eventTitle}
        facts={[
          when.toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }),
          instance.allDay
            ? 'All day'
            : when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
          instance.location,
        ]
          .filter(Boolean)
          .join(' · ')}
      />

      {instance.status === 'cancelled' && (
        <Card className="bg-surface-sunken p-3 text-sm text-content-muted">
          This date is cancelled. It stays here, and on subscribers’ calendars, marked as cancelled.
        </Card>
      )}

      <Tabs tabs={visibleTabs} value={tab} onChange={setTab}>
        {tab === 'About' && <EventDetails eventId={instance.eventId} />}
        {tab === 'Tickets' && <TicketsPanel instanceId={id} />}
        {tab === 'Who is coming' && <AttendeesPanel instanceId={id} />}
        {tab === 'Rooms' && <RoomsPanel instanceId={id} />}
      </Tabs>
    </div>
  )
}

function TicketsPanel({ instanceId }: { instanceId: string }) {
  const { data: tickets, isPending } = useTickets(instanceId)
  const [adding, setAdding] = useState(false)
  const [registering, setRegistering] = useState<Ticket | null>(null)

  if (isPending) return <Spinner />

  return (
    <div className="space-y-4">
      <div className="flex justify-end max-sm:hidden">
        <RequireAccess module="events" level="editor">
          <PageAction icon={faPlus} label="New ticket type" onClick={() => setAdding(true)}>
            Ticket type
          </PageAction>
        </RequireAccess>
      </div>

      <NewTicketForm instanceId={instanceId} open={adding} onDone={() => setAdding(false)} />
      <SidePanel
        open={registering !== null}
        onOpenChange={(next) => {
          if (!next) setRegistering(null)
        }}
        title={
          registering?.isFull
            ? `Waiting list — ${registering.name}`
            : `Register — ${registering?.name ?? ''}`
        }
      >
        {registering && (
          <RegisterForm
            key={registering.id}
            instanceId={instanceId}
            ticket={registering}
            onDone={() => setRegistering(null)}
          />
        )}
      </SidePanel>

      {!tickets || tickets.length === 0 ? (
        <EmptyState
          title="No tickets yet"
          hint="Add a ticket type and people can start registering for this date."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {tickets.map((ticket) => (
            <Card key={ticket.id} className="flex flex-col gap-2 p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="flex items-center gap-2 text-base font-semibold text-content-default">
                  <Icon icon={faTicket} className="size-4 text-content-muted" />
                  {ticket.name}
                </h3>
                {ticket.isFull ? (
                  <Chip tone="warning">Full</Chip>
                ) : (
                  <Chip tone="success">
                    {ticket.remaining === null ? 'No limit' : `${ticket.remaining} left`}
                  </Chip>
                )}
              </div>

              {ticket.description && (
                <p className="text-sm text-content-muted">{ticket.description}</p>
              )}

              <p className="text-xs text-content-subtle">
                {ticket.priceCents > 0 ? `${formatCents(cents(ticket.priceCents))} each · ` : ''}
                {ticket.taken} taken
                {ticket.capacity !== null ? ` of ${ticket.capacity}` : ''}
                {ticket.waitlistLength > 0 ? ` · ${ticket.waitlistLength} waiting` : ''}
              </p>

              <Button
                className="mt-auto"
                variant={ticket.isFull ? 'secondary' : 'primary'}
                onClick={() => setRegistering(ticket)}
              >
                {ticket.isFull ? 'Join the waiting list' : 'Register'}
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function NewTicketForm({
  instanceId,
  open,
  onDone,
}: {
  instanceId: string
  open: boolean
  onDone: () => void
}) {
  const create = useCreateTicket(instanceId)
  const blank = {
    name: '',
    capacity: '',
    maxPerRegistration: '10',
    price: '',
  }
  const [form, setForm] = useState(blank)
  const typedPrice = form.price.trim() === '' ? cents(0) : parseCents(form.price)
  const price = typedPrice !== null && typedPrice >= 0 ? typedPrice : null

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onDone()
      }}
      title="New ticket type"
      primary={
        <Button
          icon={faPlus}
          className="py-1.5"
          pending={create.isPending}
          disabled={form.name.trim() === '' || price === null}
          onClick={() =>
            create.mutate(
              {
                name: form.name.trim(),
                capacity: form.capacity === '' ? null : Number(form.capacity),
                maxPerRegistration: Number(form.maxPerRegistration) || 10,
                priceCents: price ?? 0,
              },
              {
                onSuccess: () => {
                  setForm(blank)
                  onDone()
                },
              },
            )
          }
        >
          Add ticket
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="space-y-4">
          <Field label="Name">
            <Input
              placeholder="Adult"
              value={form.name}
              onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
            />
          </Field>
          <Field label="Places" description="Blank means no limit.">
            <Input
              type="number"
              min={1}
              value={form.capacity}
              onChange={(event) => setForm((f) => ({ ...f, capacity: event.target.value }))}
            />
          </Field>
          <Field label="Price" description="Each place. Blank means free.">
            <Input
              inputMode="decimal"
              placeholder="0.00"
              className="block max-w-32 tabular-nums"
              value={form.price}
              onChange={(event) => setForm((f) => ({ ...f, price: event.target.value }))}
            />
          </Field>
          <Field label="Max per booking">
            <Input
              type="number"
              min={1}
              value={form.maxPerRegistration}
              onChange={(event) =>
                setForm((f) => ({ ...f, maxPerRegistration: event.target.value }))
              }
            />
          </Field>
        </div>
        <ErrorNote error={create.error} />
      </div>
    </SidePanel>
  )
}

/**
 * The registration form.
 *
 * When a ticket is full it says so *before* the button, and the button says
 * "join the waiting list" — nobody should press a thing labelled "register" and
 * discover afterwards that they are twelfth in a queue.
 */
function RegisterForm({
  instanceId,
  ticket,
  onDone,
}: {
  instanceId: string
  ticket: Ticket
  onDone: () => void
}) {
  const register = useRegister(instanceId)
  const [form, setForm] = useState({ name: '', email: '', quantity: '1' })
  const [outcome, setOutcome] = useState<'confirmed' | 'waitlisted' | null>(null)

  if (outcome) {
    return (
      <div className="space-y-3">
        <Chip tone={outcome === 'confirmed' ? 'success' : 'warning'} size="lg">
          {outcome === 'confirmed' ? 'You have a place' : 'You are on the waiting list'}
        </Chip>
        <p className="text-sm text-content-muted">
          {outcome === 'confirmed'
            ? `${form.name}, your ${Number(form.quantity) === 1 ? 'place is' : 'places are'} booked for ${ticket.name}.`
            : `${ticket.name} is full. If somebody cancels we will move you up automatically and email you — there is nothing else to do.`}
        </p>
        <Button variant="secondary" icon={faCheck} onClick={onDone}>
          Done
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {ticket.isFull && (
        <p className="text-sm text-warning-content">
          This ticket is full. You can still put your name down; if a place comes free you will be
          moved up and emailed.
        </p>
      )}

      <div className="space-y-4">
        <Field label="Name">
          <Input
            value={form.name}
            onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
          />
        </Field>
        <Field label="Email" description="Where we tell you if a place comes free.">
          <Input
            type="email"
            value={form.email}
            onChange={(event) => setForm((f) => ({ ...f, email: event.target.value }))}
          />
        </Field>
        <Field label="How many places">
          <Input
            type="number"
            min={1}
            max={ticket.maxPerRegistration}
            value={form.quantity}
            onChange={(event) => setForm((f) => ({ ...f, quantity: event.target.value }))}
          />
        </Field>
      </div>

      <ErrorNote error={register.error} />
      <PanelPrimary>
        <Button
          className="py-1.5"
          icon={faCheck}
          disabled={register.isPending || form.name.trim() === ''}
          onClick={() =>
            register.mutate(
              {
                registrationTypeId: ticket.id,
                contactName: form.name.trim(),
                contactEmail: form.email.trim() === '' ? null : form.email.trim(),
                quantity: Number(form.quantity) || 1,
              },
              { onSuccess: (result) => setOutcome(result.outcome) },
            )
          }
        >
          {register.isPending ? 'Saving…' : ticket.isFull ? 'Join the list' : 'Register'}
        </Button>
      </PanelPrimary>
    </div>
  )
}

/** What a registration row shows on a phone; every field shows from `sm` up. */
const REGISTRATION_FIELDS = [
  { id: 'ticket', label: 'Ticket', default: true },
  { id: 'attendees', label: 'Who is in the booking', default: false },
  { id: 'email', label: 'Email', default: false },
  { id: 'owes', label: 'What they owe', default: true },
] as const
type RegistrationField = (typeof REGISTRATION_FIELDS)[number]['id']

/** The list a registrar prints on the morning, waitlist and all. */
function AttendeesPanel({ instanceId }: { instanceId: string }) {
  const { data: registrations, isPending } = useRegistrations(instanceId)
  const cancel = useCancelRegistration(instanceId)
  const [promotedNote, setPromotedNote] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const display = useListDisplay('registrations', REGISTRATION_FIELDS)

  if (isPending) return <Spinner />
  if (!registrations || registrations.length === 0) {
    return <EmptyState title="Nobody has registered yet" />
  }

  const confirmed = registrations.filter((entry) => entry.status === 'confirmed')
  const waiting = registrations.filter((entry) => entry.status === 'waitlisted')
  const places = confirmed.reduce((total, entry) => total + entry.quantity, 0)
  const shown = registrations.find((entry) => entry.id === shownId)
  const open = (id: string) => {
    setShownId(id)
    setOpenId(id)
  }

  const cancelWith = (registration: Registration) =>
    cancel.mutate(registration.id, {
      onSuccess: (result) => {
        setOpenId(null)
        setPromotedNote(
          result.promoted.length === 0
            ? ''
            : `${result.promoted.map((entry) => entry.contactName).join(', ')} moved up from the waiting list and ${result.promoted.length === 1 ? 'has' : 'have'} been emailed.`,
        )
      },
    })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-content-muted">
          {places} {places === 1 ? 'place' : 'places'} taken by {confirmed.length}{' '}
          {confirmed.length === 1 ? 'booking' : 'bookings'}
          {waiting.length > 0 ? ` · ${waiting.length} waiting` : ''}
        </p>
        <div className="flex items-center gap-1">
          <DisplayOptions
            fields={REGISTRATION_FIELDS}
            shown={display.shown}
            onChange={display.set}
          />
          {/* A plain link, not a fetch: the browser saves the file and the
              Content-Disposition names it. Turning it into JS would mean
              re-implementing the download for no gain. */}
          <a
            href={`/api/v1/events/instances/${instanceId}/attendees-csv`}
            aria-label="Download the list"
            title="Download the list"
            className="inline-flex items-center gap-2 rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-sm font-medium text-content-default hover:bg-surface-sunken"
          >
            <Icon icon={faDownload} />
            <span className="max-sm:sr-only">Download</span>
          </a>
        </div>
      </div>

      {promotedNote && (
        <Card className="bg-success-soft p-3 text-sm text-success-content">{promotedNote}</Card>
      )}

      <Card bleed>
        <RegistrationList title="Coming" rows={confirmed} hide={display.hide} onOpen={open} />
        <RegistrationList
          title="Waiting list, in order"
          rows={waiting}
          hide={display.hide}
          onOpen={open}
        />
      </Card>

      <SidePanel
        open={openId !== null && shown !== undefined}
        onOpenChange={(next) => {
          if (!next) setOpenId(null)
        }}
        title={shown?.contactName ?? ''}
        description={
          shown
            ? `${shown.ticketName} · ${shown.quantity} ${shown.quantity === 1 ? 'place' : 'places'}`
            : undefined
        }
        footer={
          shown ? (
            <ConfirmButton
              label="Cancel the place"
              icon={faUserMinus}
              variant="quietDanger"
              className="-ml-4"
              title={`Cancel ${shown.contactName}’s place?`}
              description="If anybody is waiting, the next person is moved up and emailed straight away."
              confirmLabel="Cancel the place"
              onConfirm={() => cancelWith(shown)}
            />
          ) : undefined
        }
      >
        {shown && (
          <>
            <PanelSection title="Status">
              <Chip tone={shown.status === 'confirmed' ? 'success' : 'warning'} size="lg">
                {shown.status === 'confirmed' ? 'Coming' : 'On the waiting list'}
              </Chip>
            </PanelSection>
            {shown.contactEmail && (
              <PanelSection title="Contact">
                <a
                  href={`mailto:${shown.contactEmail}`}
                  className="inline-flex items-center gap-2 text-sm text-content-brand"
                >
                  <Icon icon={faEnvelope} />
                  {shown.contactEmail}
                </a>
              </PanelSection>
            )}
            {shown.costCents > 0 || shown.paidCents > 0 ? (
              <PaymentSection key={shown.id} instanceId={instanceId} registration={shown} />
            ) : null}
            {shown.attendees.length > 0 && (
              <PanelSection title="Who is in the booking">
                <ul className="space-y-1 text-sm text-content-default">
                  {shown.attendees.map((attendee) => (
                    <li key={attendee.name}>{attendee.name}</li>
                  ))}
                </ul>
              </PanelSection>
            )}
            <ErrorNote error={cancel.error} />
          </>
        )}
      </SidePanel>
    </div>
  )
}

function RegistrationList({
  title,
  rows,
  hide,
  onOpen,
}: {
  title: string
  rows: Registration[]
  hide: (field: RegistrationField) => string
  onOpen: (id: string) => void
}) {
  if (rows.length === 0) return null

  return (
    <section>
      <ListHeader action={<Chip tone="neutral">{rows.length}</Chip>}>{title}</ListHeader>
      <ul className="divide-y divide-border-subtle">
        {rows.map((registration) => (
          <li key={registration.id}>
            <button
              type="button"
              onClick={() => onOpen(registration.id)}
              className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-medium text-content-default">
                  {registration.contactName}
                </span>
                {registration.attendees.length > 0 && (
                  <span
                    className={`block truncate text-sm text-content-muted ${hide('attendees')}`}
                  >
                    {registration.attendees.map((attendee) => attendee.name).join(', ')}
                  </span>
                )}
                {registration.contactEmail && (
                  <span className={`block truncate text-sm text-content-muted ${hide('email')}`}>
                    {registration.contactEmail}
                  </span>
                )}
              </span>
              <span className={hide('ticket')}>
                <Chip>{registration.ticketName}</Chip>
              </span>
              {registration.quantity > 1 && <Chip tone="brand">×{registration.quantity}</Chip>}
              {registration.dueCents > 0 && (
                <span className={hide('owes')}>
                  <Chip tone="warning">Owes {formatCents(cents(registration.dueCents))}</Chip>
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Which rooms are held for this date. */
function RoomsPanel({ instanceId }: { instanceId: string }) {
  const { data: bookings, isPending } = useBookings({})

  if (isPending) return <Spinner />
  const held = (bookings ?? []).filter((booking) => booking.eventInstanceId === instanceId)

  if (held.length === 0) {
    return (
      <EmptyState
        title="No rooms booked for this date"
        hint="Book one from Facilities and attach it to this date."
      />
    )
  }

  return (
    <Card bleed>
      <ul className="divide-y divide-border-subtle">
        {held.map((booking) => (
          <li key={booking.id} className={`${listRow} flex flex-wrap items-center gap-3`}>
            <span className="min-w-0 flex-1 truncate text-base font-medium text-content-default">
              {booking.resourceName}
            </span>
            <span className="text-sm tabular-nums text-content-muted">
              {new Date(booking.startsAt).toLocaleTimeString(undefined, {
                hour: 'numeric',
                minute: '2-digit',
              })}
              –
              {new Date(booking.endsAt).toLocaleTimeString(undefined, {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
            <Chip tone={booking.status === 'approved' ? 'success' : 'warning'}>
              {booking.status === 'approved' ? 'Confirmed' : 'Awaiting approval'}
            </Chip>
          </li>
        ))}
      </ul>
    </Card>
  )
}

/**
 * Cost, paid and owed for one registration, and recording what came in — the
 * total paid so far, so typing the same amount twice changes nothing.
 */
function PaymentSection({
  instanceId,
  registration,
}: {
  instanceId: string
  registration: Registration
}) {
  const record = useRecordPayment(instanceId)
  const [paid, setPaid] = useState(() =>
    registration.paidCents > 0 ? toDecimalString(cents(registration.paidCents)) : '',
  )
  const typed = paid.trim() === '' ? cents(0) : parseCents(paid)
  const parsed = typed !== null && typed >= 0 ? typed : null
  const money = (value: number) => formatCents(cents(value))

  return (
    <PanelSection
      title="Payment"
      hint={
        registration.creditCents > 0
          ? `${money(registration.creditCents)} paid beyond what it costs now — owed back.`
          : undefined
      }
    >
      <dl className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <dt className="text-content-muted">Cost</dt>
          <dd className="font-medium tabular-nums text-content-default">
            {money(registration.costCents)}
          </dd>
        </div>
        <div>
          <dt className="text-content-muted">Paid</dt>
          <dd className="font-medium tabular-nums text-content-default">
            {money(registration.paidCents)}
          </dd>
        </div>
        <div>
          <dt className="text-content-muted">Owes</dt>
          <dd
            className={`font-medium tabular-nums ${registration.dueCents > 0 ? 'text-warning-content' : 'text-content-default'}`}
          >
            {money(registration.dueCents)}
          </dd>
        </div>
      </dl>
      <div className="flex gap-2">
        <Input
          aria-label="Paid so far"
          inputMode="decimal"
          placeholder="0.00"
          className="max-w-36 tabular-nums"
          value={paid}
          onChange={(event) => setPaid(event.target.value)}
        />
        <Button
          variant="secondary"
          icon={faCheck}
          disabled={record.isPending || parsed === null || parsed === registration.paidCents}
          onClick={() =>
            parsed !== null && record.mutate({ id: registration.id, paidCents: parsed })
          }
        >
          {record.isPending ? 'Saving…' : 'Record paid'}
        </Button>
      </div>
      <ErrorNote error={record.error} />
    </PanelSection>
  )
}
